/*
 * @Description: 订单、支付相关配置、公共方法
 * @Date: 2022-08-08 18:07:36
 */
import api from '@/api/index';

// ============= 类型定义 =============

/** 路由信息（用于来源统计，仅使用 name、path） */
export interface OrderSourceRoute {
  name?: string;
  path: string;
}

/** 未支付订单列表页来源（DMP 用，数字枚举） */
export interface OrderListSourceResult {
  source_type: number;
  source_url: string;
}

/** 未支付订单列表页来源（神策用，中文描述） */
export interface SDOrderListSourceResult {
  source_type: string;
  source_url: string;
}

/** Klarna 国家维度下的单条配置（含 currency、分期等；title 为前端写入） */
export interface KlarnaSupportedItem {
  currency?: string;
  title?: string;
  min?: number;
  max?: number;
  cycle?: number;
  term_link?: string;
  repay_cycle?: string;
}

/** 支付方式项的扩展字段（如 Klarna 国家/币种支持） */
export interface PaymentMethodExtendedField {
  klarna_supported?: Record<string, KlarnaSupportedItem[]>;
}

/** 支付方式项（与 pay_center payment_methods 接口结构一致） */
export interface PaymentMethodItem {
  payment_method?: string;
  payment_channel?: string;
  payment_type?: string;
  extended_field: PaymentMethodExtendedField;
}

// ============= 常量 =============

/** 用于订单数据：根据接口的 payment_method 得到页面展示文案 */
export const PAYMENT_METHOD_TO_USER: Record<string, string> = {
  paypal_express: 'PayPal',
  paypal: 'PayPal',
  klarna_adyen: 'Klarna',
  klarna: 'Klarna',
  adyen: 'Credit / Debit Card',
  card: 'Credit / Debit Card',
  credit: 'Credit / Debit Card',
  afterpay: 'Afterpay',
  googlepay: 'Google Pay',
  applepay: 'Apple Pay',
  cashondelivery: '', // 预留，暂时未用
  wallet: 'Store Credit',
} as const;

/** 前端常量：与数据表一致的支付方式固定值 */
export const PAY_METHODS = Object.freeze({
  PAYPAL: 'paypal',
  ADYEN: 'card',
  CARD: 'card',
  KLARNA_ADYEN: 'klarna_adyen',
  KLARNA: 'klarna',
  AFTER_PAY: 'afterpay',
  APPLE_PAY: 'applepay',
  WALLET: 'wallet',
} as const);

/** 数据表订单类型与神策名称映射 */
export const ORDER_TYPES: Readonly<Record<number, string>> = Object.freeze({
  1: '普通订单',
  3: '网红订单',
  4: '补发订单',
  5: '补差价单',
  12: '随原单补发单',
  13: '测试单',
} as const);

/** 支付相关错误提示文案 */
export const ERROR_MSG = Object.freeze({
  CANCEL: 'Payment has been cancelled or declined',
  CLOSE: 'Payment failed due to closed order',
  EXPIRE: 'Payment failed due to expired order',
  DEFAULT: 'Sorry, payment failed. Please try again',
  APPLEPAY_FAIL:
    'Apple Pay is currently unavailable. Please check your Apple account or try another payment method.',
} as const);

/** 进入订单详情页的来源类型：1 支付页 2 待支付列表页 3 外部来源（保留） 4 其他 */
const ORDER_DETAIL_SOURCE_TYPE_OTHER = 4;

/** 根据路由写入订单详情页来源类型与 URL（供详情页埋点使用） */
export function handleSourceType(route: OrderSourceRoute): void {
  let type: number = ORDER_DETAIL_SOURCE_TYPE_OTHER;
  if (route.name === 'payment') {
    type = 1;
  } else if (route.path.startsWith('/account/my-orders/unpaid')) {
    type = 2;
  }
  sessionStorage.setItem('voo-orderDetailSourceType', String(type));
  sessionStorage.setItem('voo-orderDetailSourceUrl', route.path);
}

/** 来源 path 与 DMP 来源类型数字映射 */
const FROM_PATH_TYPE: Record<string, number> = {
  '/': 1,
  '/account/myAccount': 4,
  '/cart': 5,
  '/result': 6,
  '/track-order': 7,
  '/account/myWishlist': 8,
  '/account/recentlyViewed': 9,
} as const;

/** DMP 来源类型数字与神策中文名称映射 */
const SD_SOURCE_TYPE: Record<number, string> = {
  1: '首页',
  2: '商品列表页',
  3: '商品详情页',
  4: '个人中心首页',
  5: '购物车页',
  6: '搜索结果页',
  7: 'Track Order页',
  8: '我的心愿单页',
  9: '浏览记录页',
  10: '待支付详情页',
  11: '外部页面',
  12: '其他',
} as const;

/** 未支付订单列表页 DMP 来源类型默认值 */
const ORDER_LIST_SOURCE_TYPE_OTHER = 12;
/** 未支付订单列表页 DMP 来源类型：外部 */
const ORDER_LIST_SOURCE_TYPE_EXTERNAL = 11;

/**
 * 判断未支付订单列表页的来源（DMP 用）
 * @returns source_type 数字枚举，source_url 来源 URL 或 path
 */
export function handleOrderListSource(): OrderListSourceResult {
  let source_type: number = ORDER_LIST_SOURCE_TYPE_OTHER;
  const fromPath: string | null = sessionStorage.getItem('voo-fromPath');

  if (fromPath) {
    if (fromPath.startsWith('/goods-list/')) {
      source_type = 2;
    } else if (fromPath.startsWith('/goods-detail/')) {
      source_type = 3;
    } else if (
      fromPath.startsWith('/account/order-detail/') &&
      sessionStorage.getItem('voo-enterUnpaidOrderDetail')
    ) {
      source_type = 10;
    } else if (FROM_PATH_TYPE[fromPath] !== undefined) {
      source_type = FROM_PATH_TYPE[fromPath];
    }
  } else {
    source_type = ORDER_LIST_SOURCE_TYPE_EXTERNAL;
  }

  const source_url: string =
    fromPath || (source_type === ORDER_LIST_SOURCE_TYPE_EXTERNAL ? document.referrer || '' : '');
  return { source_type, source_url };
}

/**
 * 判断未支付订单列表页的来源（神策用，中文 + 完整 URL）
 * @returns source_type 中文名称，source_url 完整 origin + path
 */
export function SDhandleOrderListSource(): SDOrderListSourceResult {
  const { source_type: rawSourceType, source_url: rawSourceUrl } = handleOrderListSource();
  const source_type: string = SD_SOURCE_TYPE[rawSourceType] ?? '其他';
  const source_url: string = rawSourceUrl.includes('http')
    ? rawSourceUrl
    : `${window.location.origin}${rawSourceUrl}`;
  return { source_type, source_url };
}

/** 订单详情页来源类型与神策名称映射 */
export const SD_SOURCE_TYPE_ORDER_DETAIL = {
  1: '支付页',
  2: '待支付订单列表页',
  3: '外部页面',
  4: '其他',
  5: '待支付订单列表页支付弹窗',
} as const;

// ============= 支付方式 =============

/** 默认支持的支付方式（接口失败或未返回时使用；klarna_adyen/klarna 默认关闭，需接口提供支持国家） */
const DEFAULT_PAYMENT_METHODS: PaymentMethodItem[] = [
  {
    payment_method: 'wallet',
    payment_channel: 'vooglam',
    payment_type: 'wallet',
    extended_field: {},
  },
  {
    payment_method: 'paypal',
    payment_channel: 'paypal',
    payment_type: 'paypal_express',
    extended_field: {},
  },
  {
    payment_method: 'card',
    payment_channel: 'adyen',
    payment_type: 'card_adyen',
    extended_field: {},
  },
  {
    payment_method: 'afterpay',
    payment_channel: 'adyen',
    payment_type: 'afterpay',
    extended_field: {},
  },
] as const;

/**
 * 获取当前支持的支付方式列表
 * 说明：Klarna 的国家、币种、分期等配置在魔方配置中，此处与 actions.js 的 getAllowPaymentMethods 不共用
 * @returns 支付方式列表；失败或空时返回默认列表
 */
export async function getAllowPaymentMethods(): Promise<PaymentMethodItem[]> {
  try {
    const { data } = await api.pay.payCenter.paymentMethodsList();
    if (data?.payment_methods?.length) {
      data.payment_methods.forEach((item: PaymentMethodItem) => {
        if (item.payment_method === PAY_METHODS.KLARNA && item.extended_field?.klarna_supported) {
          const flatList = Object.values(item.extended_field.klarna_supported).flat(
            Infinity,
          ) as KlarnaSupportedItem[];
          flatList.forEach((info: KlarnaSupportedItem) => {
            info.title = item.payment_channel ?? '';
          });
        }
      });
      return data.payment_methods;
    }
  } catch (error) {
    console.error(error);
  }
  return DEFAULT_PAYMENT_METHODS;
}

/**
 * 根据支付方式列表、国家 id、币种，得到该国家下 Klarna 对应币种的配置信息
 * @param allowPayMethods 支持的支付方式列表
 * @param country_id 国家 id（如 US、GB）
 * @param currencyName 币种名称
 * @returns 匹配的 Klarna 配置项，无则返回空对象
 */
export function getKlarnaEssentialInfo(
  allowPayMethods: PaymentMethodItem[],
  country_id: string,
  currencyName: string,
): KlarnaSupportedItem | Record<string, never> {
  const supportedInfo: Record<string, KlarnaSupportedItem[]> = {};

  allowPayMethods.forEach((item: PaymentMethodItem) => {
    if (item.payment_method === PAY_METHODS.KLARNA && item.extended_field?.klarna_supported) {
      Object.assign(supportedInfo, item.extended_field.klarna_supported);
    }
  });

  let result: KlarnaSupportedItem | Record<string, never> = {};
  if (country_id && currencyName) {
    const found = supportedInfo[country_id]?.find(
      (item: KlarnaSupportedItem) => item.currency === currencyName,
    );
    if (found) result = found;
  }
  return result;
}

/**
 * 根据已有支付方式与地址国家 id，过滤出当前应展示的支付方式（主要过滤两种 Klarna 的国别支持）
 * @param allowPayMethods 支持的支付方式列表
 * @param country_id 国家 id
 * @returns 当前国家可用的支付方式列表
 */
export function getCurrPayMethods(
  allowPayMethods: PaymentMethodItem[],
  country_id: string,
): PaymentMethodItem[] {
  return allowPayMethods.filter((item: PaymentMethodItem) => {
    if (item.payment_method === PAY_METHODS.KLARNA && item.extended_field?.klarna_supported) {
      return Object.keys(item.extended_field.klarna_supported).includes(country_id);
    }
    return true;
  });
}
